import type { ParsedPost } from './blogMarkdown';
import { postTitle, publicBlogAssetPath } from './blogMarkdown';
import { getSiteOrigin } from './siteOrigin';

const DEFAULT_META_DESCRIPTION =
  'Tips, tutorials, and product updates from Substyle — animated subtitles in Adobe Premiere Pro.';

/** Google often shows ~150–160 characters; stay inside a safe band. */
const META_DESC_HARD_MAX = 158;
const META_DESC_SOFT_MIN = 110;

const TITLE_TAG_ARTICLE_MAX = 52;

const HERO_KEYS = ['hero', 'image', 'ogImage', 'thumbnail', 'socialImage'] as const;

export type ArticleSeo = {
  /** Full title for on-page H1 (from frontmatter / slug fallback). */
  articleTitle: string;
  /** Shortened title for `<title>` / Open Graph title. */
  titleForTag: string;
  /** `titleForTag` + site suffix for document.title. */
  documentTitle: string;
  /** Tuned meta + Open Graph description. */
  metaDescription: string;
  /** Hero / intro copy under the H1 (can be longer than meta). */
  displaySummary: string;
  /** Merged keywords for meta keywords + light enrichment. */
  keywords: string | undefined;
  /** Absolute URL for og:image / JSON-LD image when configured. */
  ogImageAbsoluteUrl: string | undefined;
};

function collapseWs(s: string): string {
  return s.replace(/\s+/g, ' ').trim();
}

/** Rough plain-text extraction for meta fields (not a full Markdown parser). */
export function stripMarkdownForMeta(markdown: string): string {
  let s = markdown;
  s = s.replace(/^```[\w-]*\r?\n[\s\S]*?^```/gm, ' ');
  s = s.replace(/!\[([^\]]*)\]\([^)]+\)/g, (_, alt: string) =>
    alt ? `${alt} ` : ' '
  );
  s = s.replace(/\[([^\]]+)\]\([^)]+\)/g, '$1 ');
  s = s.replace(/^#{1,6}\s+/gm, '');
  s = s.replace(/\*\*([^*]+)\*\*/g, '$1');
  s = s.replace(/\*([^*]+)\*/g, '$1');
  s = s.replace(/__([^_]+)__/g, '$1');
  s = s.replace(/`([^`]+)`/g, '$1');
  s = s.replace(/^[-*+]\s+/gm, '');
  s = s.replace(/^\d+\.\s+/gm, '');
  return collapseWs(s);
}

export function truncateAtWord(text: string, maxLen: number): string {
  const t = text.trim();
  if (t.length <= maxLen) return t;
  const slice = t.slice(0, maxLen + 1);
  const lastSpace = slice.lastIndexOf(' ');
  const cut = lastSpace > maxLen * 0.55 ? lastSpace : maxLen;
  const out = t.slice(0, cut).trim();
  return out.endsWith('.') || out.endsWith('…') ? out : `${out}…`;
}

function firstParagraphPlain(body: string): string {
  const stripped = body.replace(/^```[\s\S]*?^```/gm, '').trim();
  const blocks = stripped.split(/\n\s*\n/);
  for (const block of blocks) {
    const line = block.split(/\r?\n/).find((l) => {
      const t = l.trim();
      return t.length > 0 && !t.startsWith('#') && !t.startsWith('```');
    });
    if (!line) continue;
    const plain = stripMarkdownForMeta(line);
    if (plain.length >= 40) return plain;
  }
  return stripMarkdownForMeta(stripped);
}

function buildMetaDescription(parsed: ParsedPost): string {
  const fmDesc = parsed.frontmatter.description?.trim();
  const bodyLead = firstParagraphPlain(parsed.body);
  let candidate = fmDesc || bodyLead || DEFAULT_META_DESCRIPTION;
  candidate = collapseWs(candidate);

  if (
    candidate.length < META_DESC_SOFT_MIN &&
    fmDesc &&
    bodyLead &&
    bodyLead.length > fmDesc.length
  ) {
    candidate = collapseWs(`${fmDesc} ${bodyLead}`);
  }

  if (candidate.length > META_DESC_HARD_MAX) {
    candidate = truncateAtWord(candidate, META_DESC_HARD_MAX);
  }
  return candidate;
}

function buildDisplaySummary(parsed: ParsedPost, metaDescription: string): string {
  const fmDesc = parsed.frontmatter.description?.trim();
  if (fmDesc && fmDesc.length >= 80) {
    return fmDesc.length > 360 ? truncateAtWord(fmDesc, 360) : fmDesc;
  }
  const bodyLead = firstParagraphPlain(parsed.body);
  if (bodyLead.length >= 60) {
    return bodyLead.length > 360 ? truncateAtWord(bodyLead, 360) : bodyLead;
  }
  return metaDescription;
}

function buildKeywords(parsed: ParsedPost): string | undefined {
  const fm = parsed.frontmatter.keywords
    ?.split(',')
    .map((s) => s.trim())
    .filter(Boolean);
  const parts = fm ? [...fm] : [];
  const enrich = ['Substyle', 'Adobe Premiere Pro', 'animated subtitles'];
  for (const phrase of enrich) {
    const has = parts.some((p) =>
      p.toLowerCase().includes(phrase.toLowerCase())
    );
    if (!has) parts.push(phrase);
  }
  const seen = new Set<string>();
  const unique: string[] = [];
  for (const p of parts) {
    if (seen.has(p)) continue;
    seen.add(p);
    unique.push(p);
  }
  if (unique.length === 0) return undefined;
  return unique.slice(0, 14).join(', ');
}

/**
 * Resolves `hero` / `image` / `ogImage` frontmatter to an absolute URL.
 * Relative filenames are resolved under `/blog_posts/<slug>/`.
 */
export function resolvePostOgImageAbsoluteUrl(
  slug: string,
  frontmatter: Record<string, string>
): string | undefined {
  for (const key of HERO_KEYS) {
    const raw = frontmatter[key]?.trim();
    if (!raw) continue;
    if (/^https?:\/\//i.test(raw)) {
      return raw;
    }
    const path = raw.startsWith('/')
      ? publicBlogAssetPath(raw)
      : publicBlogAssetPath(
          `/blog_posts/${encodeURIComponent(slug)}/${encodeURIComponent(raw)}`
        );
    const origin = getSiteOrigin().replace(/\/$/, '');
    return `${origin}${path}`;
  }
  return undefined;
}

function titleForBrowserTab(articleTitle: string): string {
  const t = collapseWs(articleTitle);
  if (t.length <= TITLE_TAG_ARTICLE_MAX) return t;
  return truncateAtWord(t, TITLE_TAG_ARTICLE_MAX);
}

/**
 * Derives search- and social-friendly metadata from frontmatter + body.
 * Use for `<title>`, meta description, Open Graph, Twitter cards, and JSON-LD.
 */
export function computeArticleSeo(parsed: ParsedPost): ArticleSeo {
  const articleTitle = postTitle(parsed);
  const titleForTag = titleForBrowserTab(articleTitle);
  const documentTitle = `${titleForTag} | Substyle blog`;
  const metaDescription = buildMetaDescription(parsed);
  const displaySummary = buildDisplaySummary(parsed, metaDescription);
  const keywords = buildKeywords(parsed);
  const ogImageAbsoluteUrl = resolvePostOgImageAbsoluteUrl(
    parsed.slug,
    parsed.frontmatter
  );

  return {
    articleTitle,
    titleForTag,
    documentTitle,
    metaDescription,
    displaySummary,
    keywords,
    ogImageAbsoluteUrl,
  };
}
